import Papa from "papaparse";
import jsPDF from "jspdf";

const BRAND = "AcceptingMD";

/* =========================================================================
   Report model
   ========================================================================= */

export type ReportCellAlign = "left" | "right";

export type ReportTable = {
  type: "table";
  heading: string;
  description?: string;
  columns: string[];
  rows: (string | number | null | undefined)[][];
  /** per-column alignment; defaults to left */
  aligns?: ReportCellAlign[];
  /** relative column widths (weights). Sum need not be 1. */
  colWeights?: number[];
};

export type ReportKpiItem = {
  label: string;
  value: string;
  hint?: string;
  tone?: "blue" | "green" | "amber" | "red" | "grey";
};

export type ReportKpi = {
  type: "kpi";
  heading: string;
  description?: string;
  items: ReportKpiItem[];
};

export type ReportBars = {
  type: "bars";
  heading: string;
  description?: string;
  bars: { label: string; value: number }[];
};

export type ReportSection = ReportTable | ReportKpi | ReportBars;

export type ReportChapter = {
  title: string;
  summary?: string;
  sections: ReportSection[];
  /** start this chapter on a new PDF page */
  pageBreakBefore?: boolean;
};

export type Report = {
  pageLabel: string;
  subtitle?: string;
  chapters: ReportChapter[];
};

/* =========================================================================
   Helpers
   ========================================================================= */

function timestamp(): string {
  const d = new Date();
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function safeFilename(s: string): string {
  return s.replace(/[^a-z0-9\-_]+/gi, "-").toLowerCase();
}

const TONE_COLOR: Record<NonNullable<ReportKpiItem["tone"]>, [number, number, number]> = {
  blue:  [22, 105, 179],
  green: [30, 124, 70],
  amber: [180, 120, 10],
  red:   [180, 45, 45],
  grey:  [148, 163, 184],
};

/* =========================================================================
   CSV export
   ========================================================================= */

export function downloadReportCsv(report: Report, filename: string): void {
  const lines: string[] = [];
  lines.push(Papa.unparse([[`Generated by ${BRAND}`]]));
  lines.push(Papa.unparse([[`Report: ${report.pageLabel}`]]));
  if (report.subtitle) lines.push(Papa.unparse([[report.subtitle]]));
  lines.push(Papa.unparse([[`Timestamp: ${timestamp()}`]]));
  lines.push("");

  report.chapters.forEach((ch) => {
    lines.push(Papa.unparse([[`### ${ch.title}`]]));
    if (ch.summary) lines.push(Papa.unparse([[ch.summary]]));
    lines.push("");

    ch.sections.forEach((section) => {
      lines.push(Papa.unparse([[`## ${section.heading}`]]));
      if (section.description)
        lines.push(Papa.unparse([[section.description]]));

      if (section.type === "table") {
        const data = [section.columns, ...section.rows.map((r) =>
          r.map((cell) => (cell == null ? "" : String(cell)))
        )];
        lines.push(Papa.unparse(data));
      } else if (section.type === "kpi") {
        const data = [
          ["Metric", "Value", "Detail"],
          ...section.items.map((i) => [i.label, i.value, i.hint || ""]),
        ];
        lines.push(Papa.unparse(data));
      } else if (section.type === "bars") {
        const total = section.bars.reduce((s, b) => s + b.value, 0);
        const data = [
          ["Category", "Value", "Percent"],
          ...section.bars.map((b) => [
            b.label,
            String(b.value),
            total ? `${((b.value / total) * 100).toFixed(1)}%` : "0.0%",
          ]),
        ];
        lines.push(Papa.unparse(data));
      }
      lines.push("");
    });
    lines.push("");
  });

  const csv = lines.join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safeFilename(filename)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* =========================================================================
   PDF export
   ========================================================================= */

type PdfContext = {
  pdf: jsPDF;
  pageWidth: number;
  pageHeight: number;
  margin: number;
  cursorY: number;
  pageLabel: string;
};

function newPdf(
  orientation: "portrait" | "landscape",
  pageLabel: string
): PdfContext {
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  return {
    pdf,
    pageWidth: pdf.internal.pageSize.getWidth(),
    pageHeight: pdf.internal.pageSize.getHeight(),
    margin: 36,
    cursorY: 0,
    pageLabel,
  };
}

function drawPageHeader(ctx: PdfContext, subtitle?: string) {
  const { pdf, pageWidth, margin } = ctx;
  const y = margin;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(22, 105, 179);
  pdf.text(BRAND, margin, y);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(107, 122, 148);
  const ts = `Generated ${timestamp()}`;
  const tsW = pdf.getTextWidth(ts);
  pdf.text(ts, pageWidth - margin - tsW, y);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(16, 42, 67);
  pdf.text(ctx.pageLabel, margin, y + 16);

  if (subtitle) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(107, 122, 148);
    pdf.text(subtitle, margin, y + 30);
  }

  pdf.setDrawColor(209, 217, 230);
  pdf.setLineWidth(0.5);
  pdf.line(
    margin,
    y + (subtitle ? 38 : 22),
    pageWidth - margin,
    y + (subtitle ? 38 : 22)
  );

  ctx.cursorY = y + (subtitle ? 52 : 36);
}

function drawPageFooter(ctx: PdfContext, pageNum?: number, total?: number) {
  const { pdf, pageWidth, pageHeight } = ctx;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(107, 122, 148);
  const n = pageNum ?? pdf.getCurrentPageInfo().pageNumber;
  const t = total ?? pdf.getNumberOfPages();
  pdf.text(
    `${BRAND} · Page ${n} of ${t}`,
    pageWidth / 2,
    pageHeight - 18,
    { align: "center" }
  );
}

function ensureSpace(ctx: PdfContext, needed: number, subtitle?: string) {
  if (ctx.cursorY + needed > ctx.pageHeight - 36) {
    ctx.pdf.addPage();
    drawPageHeader(ctx, subtitle);
  }
}

function truncate(pdf: jsPDF, text: string, maxWidth: number): string {
  if (pdf.getTextWidth(text) <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && pdf.getTextWidth(`${t}…`) > maxWidth) {
    t = t.slice(0, -1);
  }
  return `${t}…`;
}

/* -------- Chapter + section rendering -------- */

/**
 * All heading draw helpers follow the same contract: they draw inside
 * [cursorY, cursorY + advance] — nothing bleeds above cursorY and nothing
 * draws past cursorY + advance. That way callers can stack elements with
 * a simple sum of advances.
 */
function drawChapterTitle(ctx: PdfContext, chapter: ReportChapter, subtitle?: string) {
  // Fresh page if requested and the current page already has content.
  if (chapter.pageBreakBefore && ctx.pdf.getNumberOfPages() > 0 && ctx.cursorY > 80) {
    ctx.pdf.addPage();
    drawPageHeader(ctx, subtitle);
  }

  ensureSpace(ctx, 70, subtitle);
  const { pdf, margin, pageWidth } = ctx;

  const barTop = ctx.cursorY;
  const barHeight = 24;
  const titleBaseline = barTop + 18; // inside the bar's vertical span

  // Blue accent bar (contained within [barTop, barTop + barHeight])
  pdf.setFillColor(22, 105, 179);
  pdf.rect(margin, barTop, 3, barHeight, "F");

  // Title text baseline
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(16, 42, 67);
  pdf.text(chapter.title, margin + 12, titleBaseline);

  // Advance well past the bar + descenders before summary starts
  ctx.cursorY = barTop + barHeight + 14;

  if (chapter.summary) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(107, 122, 148);
    const wrapped = pdf.splitTextToSize(chapter.summary, pageWidth - margin * 2);
    wrapped.forEach((line: string) => {
      pdf.text(line, margin, ctx.cursorY);
      ctx.cursorY += 12;
    });
    ctx.cursorY += 10;
  } else {
    ctx.cursorY += 4;
  }
}

function drawSectionHeading(ctx: PdfContext, text: string, description?: string) {
  ensureSpace(ctx, description ? 44 : 28);
  const { pdf, margin } = ctx;

  const barTop = ctx.cursorY;
  const barHeight = 14;
  const textBaseline = barTop + 11; // inside the bar's vertical span

  // Left-accent bar stays inside [barTop, barTop + barHeight]
  pdf.setFillColor(22, 105, 179);
  pdf.rect(margin, barTop, 2, barHeight, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(16, 42, 67);
  pdf.text(text, margin + 8, textBaseline);

  ctx.cursorY = barTop + barHeight + 8;

  if (description) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(107, 122, 148);
    const wrapped = pdf.splitTextToSize(description, ctx.pageWidth - margin * 2);
    wrapped.forEach((line: string) => {
      pdf.text(line, margin, ctx.cursorY);
      ctx.cursorY += 11;
    });
    ctx.cursorY += 8;
  }
}

function drawTable(ctx: PdfContext, table: ReportTable) {
  const { pdf, pageWidth, margin } = ctx;
  const usableWidth = pageWidth - margin * 2;
  const cols = table.columns.length;

  let weights = table.colWeights && table.colWeights.length === cols
    ? [...table.colWeights]
    : Array(cols).fill(1);
  const weightSum = weights.reduce((s, w) => s + w, 0);
  weights = weights.map((w) => w / weightSum);
  const colWidths = weights.map((w) => w * usableWidth);
  const rowHeight = 16;
  const cellPadX = 5;

  const aligns = table.aligns && table.aligns.length === cols
    ? table.aligns
    : Array<ReportCellAlign>(cols).fill("left");

  const drawHeaderRow = () => {
    pdf.setFillColor(243, 247, 251);
    pdf.rect(margin, ctx.cursorY, usableWidth, rowHeight, "F");
    pdf.setDrawColor(209, 217, 230);
    pdf.setLineWidth(0.4);
    pdf.rect(margin, ctx.cursorY, usableWidth, rowHeight);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.setTextColor(51, 78, 104);
    let xCursor = margin;
    table.columns.forEach((c, i) => {
      const w = colWidths[i];
      const text = truncate(pdf, String(c), w - cellPadX * 2);
      const xText =
        aligns[i] === "right"
          ? xCursor + w - cellPadX - pdf.getTextWidth(text)
          : xCursor + cellPadX;
      pdf.text(text, xText, ctx.cursorY + 11);
      xCursor += w;
    });
    ctx.cursorY += rowHeight;
  };

  // Pre-flight: estimate heading + header + at least 3 body rows. If the
  // current page can't hold that, break to a fresh page BEFORE drawing the
  // heading so nothing orphans.
  const headingEstimate = 18 + (table.description ? 16 : 0);
  const minTogether = headingEstimate + rowHeight + rowHeight * 3 + 6;
  if (ctx.cursorY + minTogether > ctx.pageHeight - 36) {
    ctx.pdf.addPage();
    drawPageHeader(ctx);
  }

  drawSectionHeading(ctx, table.heading, table.description);
  drawHeaderRow();

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(16, 42, 67);

  table.rows.forEach((row, rIdx) => {
    // If there's no room for this row, new page + redraw column headers so
    // the continuation still has context.
    if (ctx.cursorY + rowHeight > ctx.pageHeight - 36) {
      ctx.pdf.addPage();
      drawPageHeader(ctx);
      // Re-render the section heading (compact) so the continuation is labeled
      drawSectionHeading(ctx, `${table.heading} (continued)`);
      drawHeaderRow();
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(16, 42, 67);
    }

    if (rIdx % 2 === 1) {
      pdf.setFillColor(251, 253, 255);
      pdf.rect(margin, ctx.cursorY, usableWidth, rowHeight, "F");
    }
    pdf.setDrawColor(231, 236, 243);
    pdf.setLineWidth(0.25);
    pdf.line(
      margin,
      ctx.cursorY + rowHeight,
      margin + usableWidth,
      ctx.cursorY + rowHeight
    );

    let x = margin;
    row.forEach((cell, i) => {
      const w = colWidths[i];
      const text = cell == null ? "" : String(cell);
      const truncated = truncate(pdf, text, w - cellPadX * 2);
      const xText =
        aligns[i] === "right"
          ? x + w - cellPadX - pdf.getTextWidth(truncated)
          : x + cellPadX;
      pdf.text(truncated, xText, ctx.cursorY + 11);
      x += w;
    });
    ctx.cursorY += rowHeight;
  });

  ctx.cursorY += 12;
}

function drawKpi(ctx: PdfContext, kpi: ReportKpi) {
  drawSectionHeading(ctx, kpi.heading, kpi.description);

  const { pdf, pageWidth, margin } = ctx;
  const usableWidth = pageWidth - margin * 2;
  const cols = Math.min(4, Math.max(2, kpi.items.length));
  const gap = 10;
  const cardWidth = (usableWidth - gap * (cols - 1)) / cols;
  const cardHeight = 70;

  let colIdx = 0;

  kpi.items.forEach((item) => {
    if (colIdx === 0) {
      ensureSpace(ctx, cardHeight + 6);
    }
    const x = margin + colIdx * (cardWidth + gap);
    const y = ctx.cursorY;

    // Card body
    pdf.setFillColor(251, 253, 255);
    pdf.rect(x, y, cardWidth, cardHeight, "F");
    pdf.setDrawColor(225, 233, 243);
    pdf.setLineWidth(0.5);
    pdf.rect(x, y, cardWidth, cardHeight);

    // Left accent stripe
    const [r, g, b] = TONE_COLOR[item.tone || "blue"];
    pdf.setFillColor(r, g, b);
    pdf.rect(x, y, 3, cardHeight, "F");

    // Label
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(107, 122, 148);
    pdf.text(truncate(pdf, item.label.toUpperCase(), cardWidth - 16), x + 10, y + 16);

    // Value
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(16, 42, 67);
    pdf.text(truncate(pdf, item.value, cardWidth - 16), x + 10, y + 40);

    // Hint
    if (item.hint) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(107, 122, 148);
      const wrapped = pdf.splitTextToSize(item.hint, cardWidth - 16);
      wrapped.slice(0, 2).forEach((line: string, i: number) => {
        pdf.text(line, x + 10, y + 54 + i * 10);
      });
    }

    colIdx += 1;
    if (colIdx >= cols) {
      colIdx = 0;
      ctx.cursorY += cardHeight + gap;
    }
  });
  if (colIdx > 0) {
    ctx.cursorY += cardHeight + gap;
  }

  ctx.cursorY += 6;
}

function drawBars(ctx: PdfContext, bars: ReportBars) {
  drawSectionHeading(ctx, bars.heading, bars.description);

  const { pdf, pageWidth, margin } = ctx;
  const usableWidth = pageWidth - margin * 2;
  const chartHeight = 110;
  const labelRowHeight = 14;
  const totalHeight = chartHeight + labelRowHeight + 8;

  ensureSpace(ctx, totalHeight);

  const chartTop = ctx.cursorY;
  const chartBottom = chartTop + chartHeight;
  const labelRow = chartBottom + 4;

  const count = bars.bars.length || 1;
  const gap = 6;
  const barWidth = (usableWidth - gap * (count - 1)) / count;
  const peak = bars.bars.reduce((m, b) => Math.max(m, b.value), 0) || 1;

  // Y-axis gridlines (0, 25%, 50%, 75%, 100%)
  pdf.setDrawColor(230, 236, 245);
  pdf.setLineWidth(0.25);
  [0, 0.25, 0.5, 0.75, 1].forEach((f) => {
    const y = chartBottom - chartHeight * f;
    pdf.line(margin, y, margin + usableWidth, y);
  });

  // Baseline
  pdf.setDrawColor(15, 80, 135);
  pdf.setLineWidth(0.6);
  pdf.line(margin, chartBottom, margin + usableWidth, chartBottom);

  // Bars
  bars.bars.forEach((b, i) => {
    const x = margin + i * (barWidth + gap);
    const h = (b.value / peak) * (chartHeight - 10);
    const y = chartBottom - h;
    pdf.setFillColor(22, 105, 179);
    pdf.rect(x, y, barWidth, h, "F");

    // Value above bar
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(16, 42, 67);
    const valText = String(b.value);
    const vW = pdf.getTextWidth(valText);
    pdf.text(valText, x + (barWidth - vW) / 2, y - 2);

    // Label below
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(107, 122, 148);
    const labelText = truncate(pdf, b.label, barWidth);
    const lW = pdf.getTextWidth(labelText);
    pdf.text(labelText, x + (barWidth - lW) / 2, labelRow + 8);
  });

  ctx.cursorY = labelRow + labelRowHeight + 4;
}

/* -------- Public PDF entry point -------- */

export function downloadReportPdf(report: Report, filename: string): void {
  // Pick orientation based on widest table anywhere in the report
  const widest = report.chapters.reduce((m, ch) => {
    return ch.sections.reduce((mm, s) => {
      if (s.type === "table") return Math.max(mm, s.columns.length);
      return mm;
    }, m);
  }, 0);
  const orientation: "portrait" | "landscape" = widest >= 6 ? "landscape" : "portrait";

  const ctx = newPdf(orientation, report.pageLabel);
  drawPageHeader(ctx, report.subtitle);

  report.chapters.forEach((chapter) => {
    drawChapterTitle(ctx, chapter, report.subtitle);
    chapter.sections.forEach((section) => {
      if (section.type === "table") drawTable(ctx, section);
      else if (section.type === "kpi") drawKpi(ctx, section);
      else if (section.type === "bars") drawBars(ctx, section);
    });
  });

  const total = ctx.pdf.getNumberOfPages();
  for (let p = 1; p <= total; p++) {
    ctx.pdf.setPage(p);
    drawPageFooter(ctx, p, total);
  }

  ctx.pdf.save(`${safeFilename(filename)}.pdf`);
}
